import type { Placement } from "@react-types/overlays";
import type { ReactNode } from "react";
import { useCallback, useRef, useState } from "react";
import type {
  ButtonProps as AriaButtonProps,
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps,
} from "react-aria-components";
import {
  Button as AriaButton,
  OverlayArrow as AriaOverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from "react-aria-components";
import { cx } from "@/utils/cx";

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEMENT_TRANSFORM_ORIGINS: Partial<Record<Placement, string>> = {
  top: "origin-bottom",
  "top left": "origin-bottom-left",
  "top right": "origin-bottom-right",
  bottom: "origin-top",
  "bottom left": "origin-top-left",
  "bottom right": "origin-top-right",
  left: "origin-right",
  "left top": "origin-top-right",
  "left bottom": "origin-bottom-right",
  start: "origin-right",
  "start top": "origin-top-left",
  "start bottom": "origin-bottom-left",
  right: "origin-left",
  "right top": "origin-top-left",
  "right bottom": "origin-bottom-left",
  end: "origin-left",
  "end top": "origin-top-left",
  "end bottom": "origin-bottom-left",
};

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseDelayedTooltipOptions {
  delay: number;
  isDisabled?: boolean;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

function useDelayedTooltip({
  delay,
  isDisabled,
  defaultOpen,
  isOpen: controlledIsOpen,
  onOpenChange,
}: UseDelayedTooltipOptions) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = controlledIsOpen ?? internalOpen;

  const open = useCallback(() => {
    setInternalOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const close = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setInternalOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleMouseEnter = useCallback(() => {
    if (isDisabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(open, delay);
  }, [isDisabled, delay, open]);

  const handleFocus = useCallback(() => {
    if (isDisabled) return;
    open();
  }, [isDisabled, open]);

  return {
    isOpen,
    triggerProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: close,
      onFocus: handleFocus,
      onBlur: close,
    },
  };
}

// ─── Components ──────────────────────────────────────────────────────────────

interface TooltipProps extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, "children"> {
  title: ReactNode;
  description?: ReactNode;
  /** @default false */
  arrow?: boolean;
  /** @default 400 */
  delay?: number;
}

export const Tooltip = ({
  title,
  description,
  children,
  arrow = false,
  delay = 400,
  closeDelay = 0,
  trigger,
  isDisabled,
  isOpen: controlledIsOpen,
  defaultOpen,
  offset = 6,
  crossOffset,
  placement = "top",
  onOpenChange,
  ...tooltipProps
}: TooltipProps) => {
  const { isOpen, triggerProps } = useDelayedTooltip({
    delay,
    isDisabled,
    defaultOpen,
    isOpen: controlledIsOpen,
    onOpenChange,
  });

  const isTopOrBottomLeft = ["top left", "top end", "bottom left", "bottom end"].includes(placement);
  const isTopOrBottomRight = ["top right", "top start", "bottom right", "bottom start"].includes(placement);
  const resolvedCrossOffset = crossOffset ?? (isTopOrBottomLeft ? -12 : isTopOrBottomRight ? 12 : 0);

  return (
    <span {...triggerProps} style={{ display: "contents" }}>
      <AriaTooltipTrigger trigger={trigger} delay={0} closeDelay={0} isDisabled={isDisabled} isOpen={isOpen}>
        {children}

        <AriaTooltip
          {...tooltipProps}
          offset={offset}
          placement={placement}
          crossOffset={resolvedCrossOffset}
          className={({ isEntering, isExiting }) =>
            cx(isEntering && "ease-out animate-in", isExiting && "ease-in animate-out")
          }
        >
          {({ isEntering, isExiting }) => (
            <div
              className={cx(
                "z-50 flex max-w-xs flex-col items-start gap-1 rounded-lg bg-primary-solid px-3 shadow-none will-change-transform",
                description ? "py-3" : "py-2",
                PLACEMENT_TRANSFORM_ORIGINS[placement],
                isEntering &&
                  "ease-out animate-in fade-in zoom-in-95 in-placement-left:slide-in-from-right-0.5 in-placement-right:slide-in-from-left-0.5 in-placement-top:slide-in-from-bottom-0.5 in-placement-bottom:slide-in-from-top-0.5",
                isExiting &&
                  "ease-in animate-out fade-out zoom-out-95 in-placement-left:slide-out-to-right-0.5 in-placement-right:slide-out-to-left-0.5 in-placement-top:slide-out-to-bottom-0.5 in-placement-bottom:slide-out-to-top-0.5",
              )}
            >
              <span className="text-xs font-semibold text-white">{title}</span>

              {description && <span className="text-xs font-medium text-tooltip-supporting-text">{description}</span>}

              {arrow && (
                <AriaOverlayArrow>
                  <svg
                    viewBox="0 0 100 100"
                    className="size-2.5 fill-bg-primary-solid in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"
                  >
                    <path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
                  </svg>
                </AriaOverlayArrow>
              )}
            </div>
          )}
        </AriaTooltip>
      </AriaTooltipTrigger>
    </span>
  );
};

interface TooltipTriggerProps extends AriaButtonProps {}

export const TooltipTrigger = ({ children, className, ...buttonProps }: TooltipTriggerProps) => {
  return (
    <AriaButton
      {...buttonProps}
      className={(values) => {
        const customClass = typeof className === "function" ? className(values) : className;
        const baseClasses = customClass?.includes("inline") ? "h-max outline-hidden" : "h-max w-max outline-hidden";
        return cx(baseClasses, customClass);
      }}
    >
      {children}
    </AriaButton>
  );
};
